/**
 * Compliance — Sanctions screening review.
 *
 * FIC Act s26A. Ledger row H2: four tables, two functions, a screening trigger
 * that has been running on every new profile since script 29 — and no operating
 * screen. A legal obligation with a queue nobody could open.
 *
 * WHY IT COULD NOT BE BUILT BEFORE, which is worth stating because it was not a
 * scheduling problem. Every RLS policy on the four sanctions tables gates on
 * `manage_sanctions_list`. That permission appeared in four policies and in no
 * row of the permissions catalogue, and both routes by which a permission
 * reaches a person — role_permissions and admin_card_permissions — carry a
 * foreign key to that catalogue. It could not be attached to a role or a card.
 * Measured: held by nobody, grantable to nobody. This screen would have
 * rendered an empty queue for every desk on the platform except the single
 * `'all'` holder, and looked like it was working.
 *
 * Script 205 registers it. Twelve more permissions are in the same state and
 * are named in the ledger.
 *
 * ---------------------------------------------------------------------------
 * THE REASON, BEFORE THE RECORD
 *
 * Unlike the provider application queue, this list is NOT reason-free. That
 * queue deliberately carries no personal data so that refreshing your own
 * workload is not an act of access. This one carries the subject's name beside
 * the name of a sanctioned individual, which is identifiable personal data
 * joined to an allegation — the most consequential pairing in the product. So
 * `manage_sanctions_list` is registered with requires_reason_capture true and
 * the ReasonGate stands in front of the fetch, not after it.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE SCREEN REFUSES TO DO
 *
 * It does not offer a fourth outcome, it does not let a verdict be edited, and
 * it does not accept reasoning under twenty characters. None of those are this
 * file's rules — the database refuses all three — but the screen states them
 * before somebody types rather than after, because being refused by Postgres
 * with the paragraph you just wrote thrown away is its own small cruelty.
 *
 * And it does not sort by date. Highest confidence first: a compliance officer
 * working a queue should meet the strongest allegation while they are freshest.
 * The ordering is the database's, for the same reason the blocker text on the
 * platform's exit screen is — a second copy of a rule is a second place for it
 * to drift.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  O, SANS, Card, StatusPill, LoadingPanel, Notice, PrimaryCTA, QuietButton,
  Field, textInputStyle,
} from '../ui';
import ReasonGate from '../ReasonGate';
import {
  fetchSanctionsQueue, recordVerdict, VERDICTS, MIN_VERDICT_NOTES,
} from '../lib/sanctions';
import SanctionsUpload from './SanctionsUpload';
import SanctionsList from './SanctionsList';

const PERMISSION = {
  permission_key: 'manage_sanctions_list',
  description: 'Review sanctions screening matches, and maintain the sanctions list itself.',
};

/**
 * Confidence, as a word rather than only a number.
 *
 * 1.00 is an exact normalised-name match; a phonetic hit sits lower. The
 * numeral stays because a compliance file that says "high" and nothing else is
 * not a file, but the word is what a person triages on.
 */
const confidenceLabel = (c) => {
  const n = Number(c);
  if (!Number.isFinite(n)) return { label: 'Unknown', tone: 'quiet' };
  if (n >= 0.95) return { label: 'Exact', tone: 'critical' };
  if (n >= 0.75) return { label: 'Strong', tone: 'warning' };
  return { label: 'Weak', tone: 'quiet' };
};

const MATCH_TYPES = {
  exact_document_hash: 'The identity document itself matched a listed document.',
  exact_name_normalised: 'The name matched exactly, once punctuation and case were removed.',
  phonetic_name: 'The name sounds the same. Spelling differs.',
  fuzzy_name: 'The name is close but not the same.',
};

const SUBJECTS = {
  member: 'Member',
  organisation: 'Organisation',
  beneficial_owner: 'Beneficial owner',
  unknown: 'Unidentified subject',
};

const Line = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 14, padding: '7px 0', fontFamily: SANS, fontSize: 12.5 }}>
    <span style={{ width: 132, flexShrink: 0, color: O.inkMuted }}>{label}</span>
    <span style={{ color: O.ink, minWidth: 0 }}>{children || '—'}</span>
  </div>
);

/** One match, and the three things that can be said about it. */
function Match({ row, onResolved }) {
  const [verdict, setVerdict] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [done, setDone] = useState(null);

  const conf = confidenceLabel(row.match_confidence);
  const tooShort = notes.trim().length < MIN_VERDICT_NOTES;
  const resolved = row.status !== 'pending_compliance_review';

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    const res = await recordVerdict({
      matchId: row.match_id, verdict, notes: notes.trim(),
    });
    setBusy(false);
    if (res.error) { setProblem(res.error); return; }
    setDone(res.data?.message || 'Recorded.');
    onResolved();
  };

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 14, marginBottom: 10,
      }}>
        <div style={{ minWidth: 0 }}>
          <p style={{
            fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
            textTransform: 'uppercase', color: O.inkFaint, margin: '0 0 5px',
          }}>{SUBJECTS[row.subject_kind] || SUBJECTS.unknown}</p>
          <p style={{ fontFamily: SANS, fontSize: 15, fontWeight: 700, color: O.ink, margin: 0 }}>
            {row.subject_name || 'Name not on file'}
          </p>
        </div>
        {/* The confidence is a mark of how strong the allegation is, and it is
            the first thing a triaging officer needs. Never colour alone: the
            word carries it and the pill carries the word. */}
        <StatusPill tone={conf.tone}>{conf.label}</StatusPill>
      </div>

      <div style={{ borderTop: `1px solid ${O.hairline}`, paddingTop: 8 }}>
        <Line label="Matched against">{row.listed_name}</Line>
        <Line label="Designation">{row.listed_designation}</Line>
        <Line label="Listed on">{row.listed_on}</Line>
        <Line label="Source notice">{row.source_notice}</Line>
        <Line label="Why it matched">
          {MATCH_TYPES[row.match_type] || row.match_type}
        </Line>
      </div>

      {resolved ? (
        <div style={{ marginTop: 12 }}>
          <Notice tone="info">
            Already resolved as {row.status.replace(/_/g, ' ')}
            {row.reviewed_by ? ` by ${row.reviewed_by}` : ''}. A reversal is a new decision,
            not an edit to this one.
          </Notice>
          {row.resolution_notes && (
            <p style={{ fontFamily: SANS, fontSize: 12, color: O.inkMuted, margin: 0, lineHeight: 1.65 }}>
              {row.resolution_notes}
            </p>
          )}
        </div>
      ) : done ? (
        <div style={{ marginTop: 12 }}><Notice tone="success">{done}</Notice></div>
      ) : (
        <div style={{ marginTop: 14, borderTop: `1px solid ${O.hairline}`, paddingTop: 14 }}>
          {problem && <Notice tone="critical">{problem}</Notice>}

          {/* Three outcomes, each with what it MEANS rather than only what it
              is called. "Confirmed match" and "false positive" are the
              database's vocabulary; they are not what a person deciding
              somebody's account should be reading off a button. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {VERDICTS.map((v) => {
              const on = verdict === v.key;
              return (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => setVerdict(v.key)}
                  aria-pressed={on}
                  style={{
                    textAlign: 'left', padding: '11px 13px', borderRadius: 11, cursor: 'pointer',
                    background: on ? 'rgba(13,61,42,0.06)' : 'transparent',
                    border: `1px solid ${on ? O.green : O.hairlineOn}`,
                    fontFamily: SANS,
                  }}
                >
                  <span style={{
                    display: 'block', fontSize: 13,
                    fontWeight: on ? 700 : 600, color: O.ink,
                  }}>{v.label}</span>
                  <span style={{
                    display: 'block', marginTop: 3, fontSize: 11.5,
                    color: O.inkMuted, lineHeight: 1.55,
                  }}>{v.blurb}</span>
                </button>
              );
            })}
          </div>

          <Field
            label="What did you check?"
            htmlFor={`notes-${row.match_id}`}
            hint={`At least ${MIN_VERDICT_NOTES} characters. This is the file the FIC reads, and the file whoever inherits this case reads.`}
          >
            <textarea
              id={`notes-${row.match_id}`}
              value={notes}
              rows={3}
              onChange={(e) => setNotes(e.target.value)}
              style={{ ...textInputStyle(false), resize: 'vertical' }}
            />
          </Field>

          <PrimaryCTA onClick={submit} busy={busy} disabled={!verdict || tooShort || busy}>
            {busy ? 'Recording…' : 'Record this decision'}
          </PrimaryCTA>
        </div>
      )}
    </Card>
  );
}

export default function SanctionsReview() {
  const [gate, setGate] = useState(true);
  const [rows, setRows] = useState(undefined);
  const [error, setError] = useState(null);
  const [showing, setShowing] = useState('pending_compliance_review');
  // Bumped when an upload lands, so the list below re-reads. See SanctionsList.
  const [writes, setWrites] = useState(0);

  const load = useCallback(async (status) => {
    setRows(undefined);
    setError(null);
    const res = await fetchSanctionsQueue(status);
    if (res.error) { setError(res.error); setRows([]); return; }
    setRows(res.data);
  }, []);

  useEffect(() => { if (!gate) load(showing); }, [gate, showing, load]);

  // THE GATE IS IN FRONT OF THE FETCH. Canon 4.2 immutable rule 3: the reason
  // is captured before the record renders, and fn_log_my_admin_action writes
  // the log entry before this component is allowed to ask for a single row.
  //
  // AND IT IS INLINE, NOT A MODAL. This section mounts when the compliance
  // card opens -- nobody pressed anything -- so a fixed full-viewport overlay
  // was an ambush, and its Cancel was `() => setGate(true)`: the flag that
  // renders it. Whoever opened the card was trapped behind a dialogue whose
  // only exit was to type a reason and write it permanently to an append-only
  // compliance log. Found in production by the founder.
  if (gate) {
    return <ReasonGate permission={PERMISSION} inline onProceed={() => setGate(false)} />;
  }

  return (
    <div>
      <p style={{
        fontFamily: SANS, fontSize: 12.5, color: O.inkMuted,
        margin: '0 0 16px', lineHeight: 1.7, maxWidth: 620,
      }}>
        Every new profile is screened against the sanctions list at the moment it is created.
        A match holds nothing on its own — it is a name that looked like another name, and
        deciding whether it is the same person is this desk&rsquo;s work. Strongest matches first.
      </p>

      {/* MAINTAINING THE LIST SITS ABOVE WORKING THE QUEUE, because the queue
          is produced BY the list and for a long time there was nothing in it:
          one row, and no function anywhere that could add another. A desk that
          opens this section and sees an empty queue needs the reason for that
          in front of them, not below the thing it explains.

          Same permission, so no second gate: `manage_sanctions_list` is
          described as "review sanctions screening matches, AND maintain the
          sanctions list itself", and the ReasonGate above already stands in
          front of this whole section. */}
      <SanctionsUpload onWritten={() => { setWrites((n) => n + 1); load(showing); }} />

      {/* AND READING IT BACK SITS DIRECTLY UNDER ADDING TO IT, because those
          two are one job. The founder: "I see that I can add a name to the
          sanctioned list, but there's nowhere for me to view all the names that
          were added historically, and remove any names." A desk that has just
          written five names and cannot see them has no way to tell whether the
          write landed on the people it meant. Same permission again, so still
          no second gate. */}
      <SanctionsList writes={writes} />

      {/* Two buttons in a row, same size, filling it. */}
      <div style={{ display: 'flex', gap: 9, marginBottom: 18 }}>
        {[
          ['pending_compliance_review', 'Waiting on you'],
          [null, 'Everything screened'],
        ].map(([key, label]) => {
          const on = showing === key;
          return (
            <div key={label} style={{ flex: '1 1 0', minWidth: 0, display: 'flex' }}>
              {on
                ? <PrimaryCTA onClick={() => setShowing(key)} style={{ width: '100%' }}>{label}</PrimaryCTA>
                : <QuietButton onClick={() => setShowing(key)} style={{ width: '100%', justifyContent: 'center' }}>{label}</QuietButton>}
            </div>
          );
        })}
      </div>

      {error && <Notice tone="critical">{error}</Notice>}

      {rows === undefined ? (
        <LoadingPanel message="Loading the review queue…" />
      ) : rows.length === 0 ? (
        // MDS 2.2: an empty queue is a SUCCESS. It is not filled with anything.
        <Notice tone="success">
          {showing
            ? 'Nothing is waiting on this desk. Every screening match has been decided.'
            : 'Nothing has been screened into a match yet.'}
        </Notice>
      ) : (
        rows.map((r) => (
          <Match key={r.match_id} row={r} onResolved={() => load(showing)} />
        ))
      )}
    </div>
  );
}
